import { useState } from 'react';
import {
  ColumnDef,
  ExpandedState,
  getCoreRowModel,
  getExpandedRowModel,
  getPaginationRowModel,
  RowSelectionState,
  useReactTable,
} from '@tanstack/react-table';
import { DeviceTableToolbar } from '@/features/devices/components/device-tree-table/device-table-toolbar.tsx';
import { DeviceTableContent } from '@/features/devices/components/device-tree-table/device-table-content.tsx';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { DeviceTablePagination } from '@/features/devices/components/device-tree-table/device-table-pagination.tsx';
import { DeviceTypeNames, TTreeDevice } from '@/features/devices/schemas/devices.schemas.ts';
import { Badge } from '@/components/ui/badge.tsx';
import { Link } from 'react-router';
import { useTranslation } from 'react-i18next';

type TTranslationFunction = {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (key: any, options?: any): string;
};

const ExpandAllButton = ({
  isExpanded,
  onToggle,
  t,
}: {
  isExpanded: boolean;
  onToggle: (event: unknown) => void;
  t: TTranslationFunction;
}) => (
  <button onClick={onToggle} className="flex items-center gap-2" type="button">
    {isExpanded ? <ChevronDown className="size-4" /> : <ChevronRight className="size-4" />}
    <span>{t('devices.deviceName')}</span>
  </button>
);

const DeviceSerialCell = ({
  depth,
  canExpand,
  isExpanded,
  isEditable,
  onToggle,
  value,
  record,
}: {
  depth: number;
  canExpand: boolean;
  isExpanded: boolean;
  isEditable: boolean;
  onToggle: () => void;
  value: string;
  record: TTreeDevice;
}) => (
  <div style={{ paddingLeft: `${depth * 1.4}rem` }}>
    <div className="flex items-center gap-2 text-nowrap">
      {canExpand ? (
        <button
          onClick={onToggle}
          className="text-muted-foreground flex items-center gap-2"
          style={{ cursor: 'pointer' }}
          type="button"
        >
          {isExpanded ? (
            <ChevronDown className="size-4" />
          ) : (
            <ChevronRight className="size-4" />
          )}
        </button>
      ) : (
        <span className="size-4" />
      )}
      {isEditable ? (
        <span>{value}</span>
      ) : !record.showInDashboard ? (
        <span>{value}</span>
      ) : (
        <Link
          to={`/plants/${record.plantId}/devices/${record.deviceId}`}
          className="link-underline text-blue-600"
        >
          {value}
        </Link>
      )}
    </div>
  </div>
);

const getLabelDevice = (deviceType: DeviceTypeNames) => {
  if (deviceType === 'Inverter') {
    return 'blue'; // Primary device
  }
  if (deviceType === 'BMS') {
    return 'purple'; // Critical management system
  }
  if (deviceType === 'SmartMeter') {
    return 'green'; // Measurement device
  }
  if (deviceType === 'SmartLogger') {
    return 'indigo'; // Data logging device
  }
  if (deviceType === 'Dongle') {
    return 'pink'; // Communication device
  }
  if (deviceType === 'Logger') {
    return 'yellow'; // Basic logging device
  }
  if (deviceType === 'PQConnect') {
    return 'red'; // Power quality device
  }
  if (deviceType === 'Battery') {
    return 'blue'; // Energy storage
  }
  if (deviceType === 'Unknown') {
    return 'gray'; // Unknown device
  }
  return 'gray'; // default fallback
};

export const getColumns = ({
  isEditable,
  t,
}: {
  isEditable: boolean;
  t: TTranslationFunction;
}): ColumnDef<TTreeDevice>[] => {
  return [
    {
      accessorKey: 'name',
      header: ({ table }) => (
        <ExpandAllButton
          isExpanded={table.getIsAllRowsExpanded()}
          onToggle={table.getToggleAllRowsExpandedHandler()}
          t={t}
        />
      ),
      cell: ({ row, getValue }) => (
        <DeviceSerialCell
          depth={row.depth}
          canExpand={row.getCanExpand()}
          isExpanded={row.getIsExpanded()}
          isEditable={isEditable}
          onToggle={row.getToggleExpandedHandler()}
          value={getValue<string>()}
          record={row.original}
        />
      ),
      footer: (props) => props.column.id,
    },
    {
      size: 100,
      accessorKey: 'SN',
      header: () => <div className="text-nowrap">{t('devices.details.serialNumber')}</div>,
      cell: ({ row }) => <div className="text-nowrap">{row.getValue('SN')}</div>,
    },
    {
      size: 100,
      accessorKey: 'type',
      header: t('devices.details.deviceType.title'),
      cell: ({ row }) => (
        <Badge className="text-nowrap" variant={getLabelDevice(row.getValue('type'))}>
          {row.getValue('type')}
        </Badge>
      ),
    },
    {
      size: 100,
      accessorKey: 'supplier',
      header: t('devices.details.supplier'),
      cell: ({ row }) => <div className="text-nowrap">{row.getValue('supplier')}</div>,
    },
    {
      size: 100,
      accessorKey: 'model',
      header: t('devices.details.model'),
      cell: ({ row }) => <div className="text-nowrap">{row.getValue('model')}</div>,
    },
  ];
};

export function DeviceTreeTable({
  data,
  setData,
  deviceInfo,
  isLoading,
}: {
  data: TTreeDevice[];
  setData?: (devices: TTreeDevice[]) => void;
  deviceInfo?: {
    companyId: string;
    plantId: string;
    timezone: string;
  };
  isLoading?: boolean;
}) {
  const [expanded, setExpandedRows] = useState<ExpandedState>({});
  const [rowSelection, setSelectedRows] = useState<RowSelectionState>({});
  const { t } = useTranslation();
  const isEditable = Boolean(setData);
  const columns = getColumns({ isEditable, t });
  const table = useReactTable({
    data,
    columns,
    state: {
      expanded,
      rowSelection,
    },
    initialState: {
      pagination: {
        pageSize: 20,
      },
    },
    autoResetPageIndex: false,
    enableRowSelection: true,
    onRowSelectionChange: setData && setSelectedRows,
    onExpandedChange: setExpandedRows,
    getSubRows: (row) => row.subDevices,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
  });

  const getSelectedTableRow = () => {
    const allRows = table.getRowModel()?.rows;
    const selectedRowId = Object.keys(rowSelection)[0];
    return allRows.find((row) => row.id === selectedRowId);
  };

  const getSelectedDevice = () => {
    const selectedRowId = Object.keys(rowSelection)[0];
    if (!selectedRowId) return null;

    const row = table.getRow(selectedRowId);
    return row.original;
  };

  const hasSelectedRow = Boolean(Object.keys(rowSelection)?.[0]);

  return (
    <div>
      <div className="w-full overflow-hidden rounded-md border">
        {setData && deviceInfo && (
          <DeviceTableToolbar
            data={data}
            deviceInfo={deviceInfo}
            hasSelectedRow={hasSelectedRow}
            getSelectedTableRow={getSelectedTableRow}
            getSelectedDevice={getSelectedDevice}
            setData={setData}
            setExpandedRows={setExpandedRows}
            setSelectedRows={setSelectedRows}
          />
        )}
        <DeviceTableContent
          table={table}
          columns={columns}
          rowSelection={rowSelection}
          setSelectedRows={setData && setSelectedRows}
          isLoading={isLoading}
        />
      </div>
      <DeviceTablePagination
        table={table}
        hasSelectedRow={hasSelectedRow}
        setSelectedRows={setData && setSelectedRows}
      />
    </div>
  );
}
